import { z } from 'zod';

// ---------------------------------------------------------------------------
// Client list item — mirrors §2.2 DTO
// ---------------------------------------------------------------------------

export const ClientListItemSchema = z.object({
  client_id: z.number(),
  tenant_cod: z.string(),
  company_name: z.string(),
  display_name: z.string().optional(),
  client_type_name: z.string().optional(),
  kyc_status_id: z.number(),
  kyc_status_name: z.string(),
  is_active: z.boolean(),
  risk_level_id: z.number(),
  risk_level_name: z.string(),
  created_at: z.string(),
  contact_email: z.string().email().optional(),
  country_code: z.string(),
  status: z.enum(['draft', 'pending', 'active', 'suspended']).optional(),
});

export type ClientListItem = z.infer<typeof ClientListItemSchema>;

// ---------------------------------------------------------------------------
// Paginated response — mirrors §2.2
// ---------------------------------------------------------------------------

export const ClientListResponseSchema = z.object({
  items: z.array(ClientListItemSchema),
  total: z.number(),
  page: z.number(),
  page_size: z.number(),
  total_pages: z.number(),
});

export type ClientListResponse = z.infer<typeof ClientListResponseSchema>;

// ---------------------------------------------------------------------------
// Filter / query state
// ---------------------------------------------------------------------------

export interface ClientListFilters {
  search: string;
  kyc_status: '' | 'pending' | 'approved' | 'rejected' | 'suspended';
  status: '' | 'draft' | 'pending' | 'active' | 'suspended';
  sort_by: 'company_name' | 'created_at';
  sort_order: 'asc' | 'desc';
  page: number;
  page_size: number;
}

export const DEFAULT_CLIENT_FILTERS: ClientListFilters = {
  search: '',
  kyc_status: '',
  status: '',
  sort_by: 'company_name',
  sort_order: 'asc',
  page: 1,
  page_size: 20,
};

// ---------------------------------------------------------------------------
// Client detail types — mirrors §2.2 DTOs (module 7.13)
// ---------------------------------------------------------------------------

export const ClientDetailSchema = z.object({
  client_id: z.number(),
  tenant_cod: z.string(),
  company_name: z.string(),
  display_name: z.string().optional(),
  client_type_name: z.string().optional(),
  legal_form_id: z.number().nullable(),
  country_code: z.string(),
  kyc_status_id: z.number(),
  kyc_status_name: z.string(),
  is_active: z.boolean(),
  risk_level_id: z.number(),
  risk_level_name: z.string(),
  created_at: z.string(),
  contact_email: z.string().email().optional(),
  telephone: z.string().optional(),
  // Business details (for Edit Client modal + KYC tab display)
  incorporation_date: z.string().optional().nullable(),
  incorporation_number: z.string().optional().nullable(),
  business_sector_id: z.number().optional().nullable(),
  business_activity_id: z.number().optional().nullable(),
  website: z.string().optional().nullable(),
  activity_description: z.string().optional().nullable(),
  registered_address_line1: z.string().optional().nullable(),
  registered_address_line2: z.string().optional().nullable(),
  registered_address_city: z.string().optional().nullable(),
  registered_address_state: z.string().optional().nullable(),
  registered_address_zip: z.string().optional().nullable(),
  registered_address_country_code: z.string().optional().nullable(),
  business_address_line1: z.string().optional().nullable(),
  business_address_line2: z.string().optional().nullable(),
  business_address_city: z.string().optional().nullable(),
  business_address_state: z.string().optional().nullable(),
  business_address_zip: z.string().optional().nullable(),
  business_address_country_code: z.string().optional().nullable(),
  tin: z.string().optional().nullable(),
  has_pep: z.boolean().optional(),
  has_us_person: z.boolean().optional(),
  // Client-level compliance fields
  purpose_of_account: z.string().optional().nullable(),
  intended_use: z.string().optional().nullable(),
  source_of_income: z.string().optional().nullable(),
  monthly_income: z.number().optional().nullable(),
  estimated_incoming_tx_count: z.number().optional().nullable(),
  estimated_incoming_volume: z.number().optional().nullable(),
  estimated_outgoing_tx_count: z.number().optional().nullable(),
  estimated_outgoing_volume: z.number().optional().nullable(),
  // Listes separees depuis #225 : d'ou l'argent est attendu n'est pas ou il
  // doit partir.
  planned_countries_in: z.array(z.string()).optional().nullable(),
  planned_countries_out: z.array(z.string()).optional().nullable(),
  // #226 — une seule liste pour les deux sens : contrairement a un pays, une
  // devise dans laquelle un client encaisse est normalement celle dans
  // laquelle il regle.
  planned_currencies: z.array(z.string()).optional().nullable(),
});

export type ClientDetail = z.infer<typeof ClientDetailSchema>;

export const PersonSchema = z.object({
  // PersonResponseDto returns numeric identifiers; mapApiPerson stringifies
  // them for the UI, so these are plain strings — not UUIDs.
  id: z.string(),
  client_id: z.string(),
  role_id: z.number().nullable().optional(),
  first_name: z.string(),
  last_name: z.string(),
  email: z.string().email(),
  gender: z.string().nullable().optional(),
  date_of_birth: z.string(),
  nationality_country_code: z.string().nullable().optional(),
  residence_country_code: z.string().nullable().optional(),
  telephone: z.string().optional().nullable(),
  ownership_percent: z.string().nullable().optional(),
  address_line1: z.string().nullable().optional(),
  address_line2: z.string().nullable().optional(),
  address_city: z.string().nullable().optional(),
  address_state: z.string().nullable().optional(),
  address_zip: z.string().nullable().optional(),
  address_country_code: z.string().nullable().optional(),
  is_pep: z.boolean().nullable().optional(),
  pep_name: z.string().nullable().optional(),
  pep_relationship: z.string().nullable().optional(),
  us_resident: z.boolean().nullable().optional(),
  us_tin: z.string().nullable().optional(),
  residence_tin: z.string().nullable().optional(),
  is_active: z.boolean(),
  created_at: z.string(),
  has_portal_access: z.boolean(),
  // Optional during the rollout window — older webapp deploys may not yet
  // emit this. UI treats missing as `false`.
  invitation_active: z.boolean().optional(),
  screening_status_id: z.number().nullable().optional(),
  // Retained for backward compatibility — a few call sites still reference
  // `phone` directly; new code should use `telephone`.
  phone: z.string().optional().nullable(),
});

export type Person = z.infer<typeof PersonSchema>;

export const PersonRightsSchema = z.object({
  person_id: z.string().uuid(),
  can_view_transactions: z.boolean(),
  can_create_transfer: z.boolean(),
  /** #62 — approuver, et donc envoyer, un paiement préparé par un autre. */
  can_approve_payment: z.boolean(),
  can_create_fx: z.boolean(),
  can_manage_beneficiary: z.boolean(),
  created_at: z.string(),
});

export type PersonRights = z.infer<typeof PersonRightsSchema>;

export const BeneficiarySchema = z.object({
  // BeneficiaryResponseDto returns numeric identifiers; mapApiBeneficiary
  // stringifies them for the UI, so these are plain strings — not UUIDs.
  id: z.string(),
  client_id: z.string(),
  /** Client display name — only the pending-screening queue is cross-client. */
  client_name: z.string().nullable().optional(),
  beneficiary_type_id: z.number().nullable().optional(),
  beneficiary_name: z.string(),
  company_name: z.string().nullable().optional(),
  first_name: z.string().nullable().optional(),
  last_name: z.string().nullable().optional(),
  account_number: z.string(),
  bic: z.string().optional().nullable(),
  country_code: z.string(),
  bank_name: z.string().optional().nullable(),
  bank_country_code: z.string().nullable().optional(),
  currency_code: z.string().optional().nullable(),
  bank_network_id: z.number().optional().nullable(),
  bank_code: z.string().nullable().optional(),
  address_line1: z.string().nullable().optional(),
  address_line2: z.string().nullable().optional(),
  address_city: z.string().nullable().optional(),
  address_state: z.string().nullable().optional(),
  address_zip: z.string().nullable().optional(),
  address_country_code: z.string().nullable().optional(),
  is_active: z.boolean(),
  created_at: z.string(),
  /**
   * Retained so the iban-or-account display fallback keeps working during
   * the transition — `account_number` holds the authoritative value and is
   * what gets submitted. `iban` is only surfaced when the stored value
   * looks like a valid IBAN.
   */
  iban: z.string().optional().nullable(),
  // Champs du Payment Formatting Guide (#70). Facultatifs et nullables :
  // le corridor decide lesquels Convera reclame, et les lignes creees
  // avant #60 n'en portent aucun.
  tax_id: z.string().nullish(),
  email: z.string().nullish(),
  phone_number: z.string().nullish(),
  date_of_birth: z.string().nullish(),
  bank_branch_code: z.string().nullish(),
  bank_account_type: z.string().nullish(),
  bank_branch_name: z.string().nullish(),
  bank_address_line1: z.string().nullish(),
  // Response-only: preserved from the API, not edited or sent by the form.
  bank_address_line2: z.string().nullish(),
  bank_address_line3: z.string().nullish(),
  bank_address_city: z.string().nullish(),
  bank_address_state: z.string().nullish(),
  bank_address_zip: z.string().nullish(),
});

export type Beneficiary = z.infer<typeof BeneficiarySchema>;

export const ClientAccountSchema = z.object({
  id: z.string().uuid(),
  client_id: z.string().uuid(),
  currency: z.string(),
  iban: z.string(),
  available_balance: z.string(),
  pending_in_balance: z.string(),
  pending_out_balance: z.string(),
  reserved_balance: z.string(),
  created_at: z.string(),
  wallet_id: z.union([z.number(), z.string()]).optional(),
});

export type ClientAccount = z.infer<typeof ClientAccountSchema>;

export const TransactionSummarySchema = z.object({
  id: z.string().uuid(),
  type: z.enum(['payment', 'fx', 'incoming']),
  direction: z.enum(['outgoing', 'incoming']),
  amount: z.string(),
  currency: z.string(),
  counterparty: z.string(),
  status: z.string(),
  executed_at: z.string(),
});

export type TransactionSummary = z.infer<typeof TransactionSummarySchema>;

export const AuditLogEntrySchema = z.object({
  id: z.string().uuid(),
  client_id: z.string().uuid(),
  user_id: z.string().uuid(),
  action: z.string(),
  details: z.string(),
  created_at: z.string(),
});

export type AuditLogEntry = z.infer<typeof AuditLogEntrySchema>;

export const TRANSFER_FEE_TYPE = {
  FIXED: 1,
  PERCENTAGE: 2,
} as const;

export type TransferFeeTypeId = (typeof TRANSFER_FEE_TYPE)[keyof typeof TRANSFER_FEE_TYPE];

/**
 * One payment rail's fee set (#66). Fees are cumulative: a fixed part plus a
 * percentage, set independently for incoming and outgoing. All four may be
 * zero — a free rail is a commercial term, not a missing configuration.
 */
export const ServicePricingSchema = z.object({
  payment_service_id: z.number(),
  payment_service_code: z.string(),
  outgoing_fee_fixed_value: z.string(),
  outgoing_fee_percent_value: z.string(),
  incoming_fee_fixed_value: z.string(),
  incoming_fee_percent_value: z.string(),
  is_active: z.boolean(),
  updated_at: z.string(),
});

export type ServicePricing = z.infer<typeof ServicePricingSchema>;

/**
 * A client's whole tariff. `fx_spread` sits beside the list rather than inside
 * it: exchange is a service of its own, so the spread applies whichever rail
 * the converted funds then take.
 */
export const ClientPricingSchema = z.object({
  client_id: z.number(),
  fx_spread: z.string(),
  services: z.array(ServicePricingSchema),
});

export type ClientPricing = z.infer<typeof ClientPricingSchema>;

// ---------------------------------------------------------------------------
// Screening status mapping (from webapp seeder seed-lookups.ts)
// ---------------------------------------------------------------------------

export const SCREENING_STATUS = {
  PENDING: 1,
  APPROVED: 2,
  REJECTED: 3,
} as const;

export const SCREENING_STATUS_LABELS: Record<number, string> = {
  [SCREENING_STATUS.PENDING]: 'Pending',
  [SCREENING_STATUS.APPROVED]: 'Approved',
  [SCREENING_STATUS.REJECTED]: 'Rejected',
};

export type ScreeningBadgeVariant = 'warning' | 'success' | 'error' | 'default';

export const SCREENING_BADGE_VARIANT: Record<number, ScreeningBadgeVariant> = {
  [SCREENING_STATUS.PENDING]: 'warning',
  [SCREENING_STATUS.APPROVED]: 'success',
  [SCREENING_STATUS.REJECTED]: 'error',
};

// ---------------------------------------------------------------------------
// Document types — mirrors webapp DocumentDto
// ---------------------------------------------------------------------------

export const DocumentItemSchema = z.object({
  documentId: z.number(),
  clientId: z.number(),
  personId: z.number().nullable(),
  documentTypeId: z.number(),
  name: z.string(),
  mimeTypeId: z.number(),
  size: z.number().nullable(),
  documentStatusId: z.number(),
  expiryDate: z.string().nullable(),
  uploadedBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type DocumentItem = z.infer<typeof DocumentItemSchema>;

// ---------------------------------------------------------------------------
// Tab definitions for client detail page
// ---------------------------------------------------------------------------

export const CLIENT_DETAIL_TABS = [
  'kyc',
  'persons',
  'beneficiaries',
  'pricing',
  'accounts',
  'documents',
  'history',
  'audit-log',
] as const;

export type ClientDetailTab = (typeof CLIENT_DETAIL_TABS)[number];

export const TAB_LABELS: Record<ClientDetailTab, string> = {
  kyc: 'KYC',
  persons: 'Persons',
  beneficiaries: 'Beneficiaries',
  pricing: 'Pricing',
  accounts: 'Accounts',
  documents: 'Documents',
  history: 'History',
  'audit-log': 'Audit Log',
};

/**
 * Permission codes that unlock each tab. The operator passes when they carry
 * ANY of the listed codes. Keep aligned with the backend route policies — a
 * tab the operator can see should map to API endpoints they can actually call.
 */
export const TAB_PERMISSIONS: Record<ClientDetailTab, readonly string[]> = {
  kyc: ['MANAGE_CLIENTS'],
  // CREATE_CLIENT is the onboarding-flow permission and does NOT grant read
  // access to existing clients (backend GET /clients/:clientId/persons
  // requires MANAGE_CLIENTS). Keeping CREATE_CLIENT here let onboarding-only
  // operators open the Persons tab and immediately 403 on the API call.
  persons: ['MANAGE_CLIENTS'],
  beneficiaries: ['MANAGE_BENEFICIARIES'],
  pricing: ['MANAGE_PRICING'],
  accounts: ['MANAGE_CLIENTS'],
  documents: ['MANAGE_DOCUMENTS'],
  // Backend GET /clients/:id/transactions requires VIEW_AUDIT only — a
  // MANAGE_CLIENTS-only operator must not see this tab.
  history: ['VIEW_AUDIT'],
  'audit-log': ['VIEW_AUDIT'],
};
